<template>
  <div class="contact">
    <BackgroundImage
      class="thin left"
      :data="data?.fullWidthImage"
    />

    <div>
      <div class="contact-intro">
        <h2>{{ copy.title }}</h2>
        <p>{{ copy.text }}</p>
      </div>
      <form
        class="contact-form"
        @submit.prevent="submitForm"
      >
        <input
          v-model="name"
          type="text"
          placeholder="Name"
          v-bind="showInvalidName ? { style: 'border: 1px solid red' } : {}"
        />
        <input
          v-model="email"
          type="text"
          placeholder="Email"
          v-bind="showInvalidEmail ? { style: 'border: 1px solid red' } : {}"
        />
        <textarea
          v-model="message"
          type="text"
          placeholder="Message"
          v-bind="showInvalidMessage ? { style: 'border: 1px solid red' } : {}"
          maxlength="1000"
        />
        <button
          v-if="!messageEmitted"
          type="submit"
          class="contact-submit"
        >
          <span class="contact-submit__sparkle"
aria-hidden="true">✦</span>
          Send
        </button>
        <div
          v-else
          class="success-message"
        >
          Message sent successfully!
        </div>
        <div
          v-if="messageFailed"
          class="error-message"
          style="color: red;"
        >
          Message failed to send. Please try again later.
        </div>
      </form>
    </div>

    <RotatingWords
      class="bg-dark"
      :data="data?.rotatingWords"
    />
  </div>
</template>

<script>
import axios from 'axios'
import { mapState } from 'pinia'
import { useStore } from '@/stores/index'
import { apollo } from '@/mixins'
import { getContactContent } from '@/data/contact'

import BackgroundImage from '@/components/Images/BackgroundImageParallax.vue'
import RotatingWords from '@/components/Text/RotatingWords.vue'

export default {
  name: 'ContactView',
  mixins: [apollo],
  components: {
    BackgroundImage,
    RotatingWords
  },
  data() {
    return {
      apolloId: 'contact',
      messageEmitted: false,
      messageFailed: false,
      name: '',
      email: '',
      message: '',
      showInvalidName: false,
      showInvalidEmail: false,
      showInvalidMessage: false,
      backendEndpoint: this.backendEndpoint,
    }
  },
  computed: {
    ...mapState(useStore, ['language']),
    copy() {
      return getContactContent(this.language)
    },
  },
  methods: {
    async submitForm() {
      this.showInvalidName = false
      this.showInvalidEmail = false
      this.showInvalidMessage = false

      const name = this.name.trim()
      const email = this.email.trim()
      const message = this.message.trim()
      const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

      if (name.length < 2) {
        this.showInvalidName = true
        return
      }

      if (!isValidEmail) {
        this.showInvalidEmail = true
        return
      }

      if (message.length < 2) {
        this.showInvalidMessage = true
        return
      }

      try {
        await axios.post(this.backendEndpoint, {
          query: `
            mutation sendSupportQuery($name: Name!, $email: Email!, $message: String!) {
              sendSupportQuery(name: $name, email: $email, message: $message)
            }
          `,
          variables: {
            name,
            email,
            message
          }
        }, {
          headers: {
            'Content-Type': 'application/json'
          }
        })

        this.messageEmitted = true
      } catch (error) {
        this.messageFailed = true
      }
    }
  }
}
</script>

<style
  lang="scss"
  scoped
>
.contact-form {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 480px;
  box-sizing: border-box;
  margin: 0 auto;
  padding: 24px;
  background: var(--bzioup-bg-color-A);
  border-radius: $border-radius;
  box-shadow: var(--bzioup-shadow-soft);

  input,
  textarea {
    width: 100%;
    box-sizing: border-box;
    margin: 8px 0;
    padding: 12px 14px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: $border-radius;
    background: var(--bzioup-bg-color-A);
    color: var(--bzioup-color-Y);
    font-size: 14px;
    transition: border-color 180ms ease-out, box-shadow 180ms ease-out;
  }

  input:focus,
  textarea:focus {
    outline: none;
    border-color: var(--bzioup-color-pink);
    box-shadow: 0 0 0 3px var(--bzioup-color-pink-soft);
  }

  textarea {
    height: 150px;
    resize: none;
    font-family: $font-family;
  }
}

@media (max-width: $mq-small) {
  .contact-form {
    padding: 16px;
  }

  .contact-form textarea {
    height: 120px;
  }
}

/* Submit button: brand-pink pill with an animated sparkle on the
   left edge. Same animation register as the pricing page's Popular
   pill (slow shadow breathe + faster sparkle rotate), so the page
   has the same visual signature. */
.contact-intro {
  max-width: 480px;
  margin: 0 auto 28px;
  text-align: center;

  h2 {
    margin: 0 0 10px;
  }

  p {
    margin: 0;
    font-size: 16px;
    line-height: 1.55;
    color: var(--bzioup-color-Y);
  }
}

.contact-submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 14px 0 4px;
  padding: 12px 18px;
  background: var(--bzioup-color-pink);
  color: white;
  border: none;
  border-radius: 999px;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.3px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(218, 138, 139, 0.4);
  animation: contact-submit-breathe 3.2s ease-in-out infinite;
  transition: transform 150ms ease-out, opacity 150ms ease-out;
}

.contact-submit:hover {
  transform: translateY(-1px);
  opacity: 0.95;
}

.contact-submit__sparkle {
  display: inline-block;
  font-size: 14px;
  line-height: 1;
  transform-origin: center;
  animation: contact-submit-sparkle 2s ease-in-out infinite;
}

@keyframes contact-submit-breathe {
  0%, 100% { box-shadow: 0 2px 6px rgba(218, 138, 139, 0.4); }
  50%      { box-shadow: 0 3px 14px rgba(218, 138, 139, 0.65); }
}

@keyframes contact-submit-sparkle {
  0%, 100% { transform: rotate(0deg) scale(1); }
  50%      { transform: rotate(180deg) scale(1.2); }
}

/* Success + error states stay clean. soft green for success, soft
   red for failure (the inline `style="color: red;"` on the error
   div is left as-is so it survives this revamp without conflicts). */
.success-message {
  margin: 10px 0;
  padding: 10px 14px;
  border-radius: $border-radius;
  background: rgba(46, 160, 67, 0.1);
  color: #2ea043;
  text-align: center;
  font-size: 14px;
}

.error-message {
  margin: 10px 0;
  padding: 10px 14px;
  border-radius: $border-radius;
  background: rgba(218, 138, 139, 0.15);
  text-align: center;
  font-size: 14px;
}
</style>